/**
 * @theme/section-renderer — fetches section HTML from Shopify's Section Rendering API and applies it via morph.
 *
 * Shopify's Section Rendering API returns the rendered HTML of one or more sections at any URL by
 * appending `?sections=section-id-1,section-id-2`. This module wraps the fetch + parse + morph cycle and
 * coalesces concurrent requests so we don't fetch the same section twice in flight.
 *
 * Usage:
 *
 *   import { SectionRenderer, morphSection } from '@theme/section-renderer';
 *
 *   // GET request: render `cart-drawer` and `cart-icon-bubble` from the current cart page.
 *   const html = await SectionRenderer.render(['cart-drawer', 'cart-icon-bubble']);
 *   morphSection('cart-drawer', html['cart-drawer']);
 *   morphSection('cart-icon-bubble', html['cart-icon-bubble']);
 *
 *   // POST cart mutation, then morph from the response sections map.
 *   const data = await SectionRenderer.cartFetch('/cart/add.js', body, ['cart-drawer','cart-icon-bubble']);
 *   morphSection('cart-drawer', data.sections['cart-drawer']);
 */

import { morphBySectionId } from '@theme/morph';
import { ThemeEvents, SectionRenderedEvent, CartUpdateEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * @typedef {{ sections: Record<string, string>, [key: string]: any }} CartApiResponse
 */

/** @type {Map<string, Promise<Record<string, string>>>} */
const __inflight = new Map();

export const SectionRenderer = {
  /**
   * Render one or more sections from the current page.
   * @param {string | string[]} sectionIds
   * @param {{ url?: string, signal?: AbortSignal }} [opts]
   * @returns {Promise<Record<string, string>>}
   */
  async render(sectionIds, opts = {}) {
    const ids = Array.isArray(sectionIds) ? sectionIds : [sectionIds];
    const url = new URL(opts.url || window.location.href, window.location.origin);
    url.searchParams.set('sections', ids.join(','));
    const cacheKey = url.toString();

    if (__inflight.has(cacheKey)) return __inflight.get(cacheKey);

    const promise = (async () => {
      try {
        const res = await fetch(url, { signal: opts.signal, credentials: 'same-origin' });
        if (!res.ok) throw new Error(`Section render failed: ${res.status}`);
        return /** @type {Record<string, string>} */ (await res.json());
      } finally {
        __inflight.delete(cacheKey);
      }
    })();

    __inflight.set(cacheKey, promise);
    return promise;
  },

  /**
   * POST to a Cart API endpoint with `sections` set; returns the parsed response.
   * Dispatches CartUpdateEvent on success and CartErrorEvent on failure.
   *
   * @param {'/cart/add.js' | '/cart/change.js' | '/cart/update.js' | string} endpoint
   * @param {object} body
   * @param {string[]} sectionsToRender
   * @param {{ source?: 'add'|'remove'|'change'|'replace' }} [meta]
   * @returns {Promise<CartApiResponse>}
   */
  async cartFetch(endpoint, body, sectionsToRender = [], meta = {}) {
    const init = {
      ...fetchConfig('json'),
      body: JSON.stringify({ ...body, sections: sectionsToRender, sections_url: window.location.pathname }),
      credentials: 'same-origin',
    };
    try {
      const res = await fetch(endpoint, init);
      const data = /** @type {CartApiResponse} */ (await res.json());
      if (!res.ok) {
        const err = data?.description || data?.message || `Cart request failed: ${res.status}`;
        document.dispatchEvent(new CartErrorEvent({ message: err, status: res.status, source: endpoint, error: data }));
        throw Object.assign(new Error(err), { status: res.status, data });
      }
      // Bubble a generic update event; subscribers can read sections off detail.
      document.dispatchEvent(new CartUpdateEvent({
        itemCount: typeof data.item_count === 'number' ? data.item_count : (data?.items?.length ?? 0),
        items: Array.isArray(data.items) ? data.items : undefined,
        source: meta.source ?? (endpoint.includes('add') ? 'add' : endpoint.includes('change') ? 'change' : endpoint.includes('update') ? 'change' : 'replace'),
        sectionsToRender,
      }));
      return data;
    } catch (err) {
      // CartErrorEvent already dispatched if it was an HTTP error; for network errors dispatch here.
      if (!(err instanceof Error) || !('status' in /** @type {any} */ (err))) {
        document.dispatchEvent(new CartErrorEvent({
          message: err instanceof Error ? err.message : 'Network error',
          source: endpoint,
          error: err,
        }));
      }
      throw err;
    }
  },
};

/**
 * Morph the current section element by id from raw HTML returned by the Section Rendering API.
 * Dispatches `theme:section:rendered`.
 *
 * @param {string} sectionId
 * @param {string} html
 * @param {{ source?: 'cart'|'facets'|'variant'|'manual' }} [opts]
 * @returns {Element | null}
 */
export function morphSection(sectionId, html, opts = {}) {
  const result = morphBySectionId(sectionId, html);
  if (result) {
    document.dispatchEvent(new SectionRenderedEvent({ sectionId, source: opts.source || 'manual' }));
  }
  return result;
}
